import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Input, Select, Spin } from 'antd';
import { PoweroffOutlined, SearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import queryString from 'query-string';
import _ from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { userInfoVar } from 'src/apollo/cache';
import LeftMenu2 from 'src/components/LevelUp/LeftMenu/index2';
import { Carousel3 } from 'src/components/LevelUp/Carousel';
import { FAVORITE_BOOK } from 'src/operations/mutations/externalUserStudy';
import { EXTERNAL_STUDY_BOOK } from 'src/operations/queries/externalStudy';
import { GET_STUDENT_LUPUSER, GET_TEACHER_USER } from 'src/operations/queries/getUser';
import { getLixileScoreObject } from 'src/utils/index';

const { Option } = Select;

const LEVEL_TITLES = {
  1: 'Beginner',
  2: 'Early',
  3: 'Developing',
  4: 'Progressing',
  5: 'Confident',
  6: 'Advanced',
  7: 'Proficient',
  8: 'Fluent',
  9: 'Articulate',
  10: 'Accomplished',
  11: 'Distinguished',
  12: 'Mastery',
};

const RowFlex = styled.div`
  display: flex;
  flex-direction: row;
`;

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  padding-top: ${({ $isMobile }) => ($isMobile ? '0' : '64px')};
  overflow: ${({ $isMobile }) => ($isMobile ? 'auto' : 'hidden')};
  box-sizing: border-box;
  background:
    radial-gradient(ellipse 900px 520px at 15% 30%, rgba(34, 211, 238, 0.08) 0%, transparent 70%),
    radial-gradient(ellipse 700px 420px at 85% 70%, rgba(16, 185, 129, 0.06) 0%, transparent 70%),
    #e8f4f8;
`;

const MobileHeader = styled(RowFlex)`
  justify-content: space-between;
  align-items: center;
  height: 64px;
  padding: 0 12px;
  background-color: #ffffff;
  border-bottom: 1px solid rgba(8, 145, 178, 0.1);
`;

const MobileSpacer = styled.div`
  width: 34px;
  height: 34px;
`;

const MobileIconButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: rgba(8, 145, 178, 0.1);
  color: #16576b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const MobileSearchBar = styled.div`
  padding: 6px 12px;
  margin-top: -14px;
  background: #ffffff;
`;
const MobileAppHeader = styled(RowFlex)`
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 14px;
  background: linear-gradient(135deg, #0c1a2e 0%, #1e3a5f 60%, #0891b2 100%);
  position: sticky;
  top: 0;
  z-index: 30;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 18px 18px;
    pointer-events: none;
  }
 `;
const MobileHeaderBrand = styled(RowFlex)`
  position: relative;
  z-index: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;
const MobileHeaderLogo = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0891b2, #06b6d4);
  background-image: url('/images/LevelUp/levelup_owl.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 24px 24px;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.4);
  flex-shrink: 0;
`;
const MobileHeaderName = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 18px;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  span {
    color: #22d3ee;
  }
`;
const MobileHeaderActions = styled(RowFlex)`
  position: relative;
  z-index: 1;
  gap: 8px;
  align-items: center;
`;
const MobileHeaderRound = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 0;
`;
const MobilePage = styled.div`
  width: 100%;
  padding: 0 0 calc(118px + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const MobilePageInner = styled.div`
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const MobileTopPanel = styled.div`
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.92);
  border: 1.5px solid rgba(255, 255, 255, 0.75);
  box-shadow: 0 14px 30px rgba(8, 145, 178, 0.12);
  backdrop-filter: blur(10px);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const MobileLevelRail = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const MobileLevelChip = styled.button`
  min-width: 82px;
  height: 34px;
  border-radius: 17px;
  border: 1.5px solid ${(props) => (props.$active ? 'transparent' : 'rgba(8,145,178,.16)')};
  background: ${(props) => (props.$active ? 'linear-gradient(135deg,#0891b2,#0ea5e9)' : '#ffffff')};
  color: ${(props) => (props.$active ? '#ffffff' : '#4a6380')};
  box-shadow: ${(props) => (props.$active ? '0 8px 18px rgba(8,145,178,.22)' : 'none')};
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
  flex-shrink: 0;
`;
const MobileTopicRail = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const MobileControlCard = styled.div`
  border-radius: 22px;
  background: #ffffff;
  border: 1.5px solid rgba(8, 145, 178, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const MobileGenreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const MobileGenreScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  min-width: 0;
  flex: 1;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const MobileFilterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const MobileBookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
`;
const MobileBottomNav = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  background: #ffffff;
  border-top: 1.5px solid rgba(8, 145, 178, 0.12);
  box-shadow: 0 -4px 20px rgba(8, 145, 178, 0.1);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  z-index: 40;
`;
const MobileBottomNavItem = styled.button`
  padding: 10px 4px 8px;
  border: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: ${(props) => (props.$active ? '#0891b2' : '#8fa3bb')};
  font-size: 10px;
  font-weight: 700;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  min-height: calc(100vh - 64px);
  height: calc(100vh - 64px);
  width: 100%;
  overflow: hidden;
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: ${({ $isMobile }) => ($isMobile ? '12px' : '12px 24px 20px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  box-sizing: border-box;
`;

const Hero = styled.div`
  display: flex;
  align-items: stretch;
  gap: 18px;
  padding: 22px 24px;
  border-radius: 26px;
  background: linear-gradient(135deg, #0891b2 0%, #14b8a6 100%);
  box-shadow: 0 10px 24px rgba(8, 145, 178, 0.18);
  color: #ffffff;
  flex-shrink: 0;
  @media (max-width: 768px) {
    padding: 18px;
    gap: 14px;
  }
`;

const HeroBadge = styled.div`
  min-width: 110px;
  padding: 12px 18px;
  border-radius: 18px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  backdrop-filter: blur(6px);
`;

const HeroBadgeNum = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 34px;
  font-weight: 900;
  line-height: 1;
`;

const HeroBadgeLabel = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 4px;
`;

const HeroInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroTitle = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 24px;
  font-weight: 900;
  line-height: 1.15;
  margin-bottom: 8px;
`;

const HeroChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const HeroChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  font-size: 11px;
  font-weight: 800;
`;

const HeroStats = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  min-width: 0;
  justify-content: center;
  align-self: stretch;
  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const HeroStat = styled.div`
  flex: 1 1 0;
  min-width: 132px;
  padding: 12px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeroStatValue = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 22px;
  font-weight: 900;
  line-height: 1;
`;

const HeroStatLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 3px;
`;

const HeroStatHint = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 8px;
  line-height: 1.2;
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 22px;
  background: #ffffff;
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  box-shadow: 0 2px 12px rgba(8, 145, 178, 0.12);
  flex-shrink: 0;
  @media (max-width: 1100px) {
    flex-wrap: wrap;
  }
`;

const GenreScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  flex: 1;
  min-width: 0;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(8, 145, 178, 0.22);
    border-radius: 999px;
  }
`;

const GenreRail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const GenreArrowButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  color: #0891b2;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.1);
  &:hover {
    background: #0891b2;
    color: #ffffff;
    border-color: #0891b2;
  }
`;

const GenreTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 20px;
  border: 1.5px solid ${(props) => (props.$active ? 'rgba(8,145,178,.28)' : 'rgba(8,145,178,.12)')};
  background: ${(props) => (props.$active ? 'rgba(8,145,178,.08)' : '#ffffff')};
  color: ${(props) => (props.$active ? '#0891b2' : '#4a6380')};
  font-size: 12px;
  font-weight: ${(props) => (props.$active ? 800 : 700)};
  white-space: nowrap;
  cursor: pointer;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  border: 1.5px solid ${(props) => (props.$active ? 'rgba(249,115,22,.25)' : 'rgba(8,145,178,.12)')};
  background: ${(props) => (props.$active ? 'rgba(249,115,22,.12)' : '#ffffff')};
  color: ${(props) => (props.$active ? '#f97316' : '#4a6380')};
  cursor: pointer;
`;

const SearchInput = styled(Input)`
  width: 200px;
  height: 38px;
  border-radius: 20px;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border-color: rgba(8, 145, 178, 0.22);
    box-shadow: none;
  }
`;

const SortSelect = styled(Select)`
  width: 150px;
  .ant-select-selector {
    height: 38px !important;
    border-radius: 20px !important;
    border: 1.5px solid rgba(8, 145, 178, 0.14) !important;
    display: flex;
    align-items: center;
    box-shadow: none !important;
  }
`;

const BookArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(8, 145, 178, 0.06);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(8, 145, 178, 0.62), rgba(6, 182, 212, 0.52));
    border-radius: 999px;
  }
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px 12px;
  color: #4a6380;
  font-size: 13px;
  font-weight: 700;
`;

const BookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  padding-bottom: 8px;
  @media (max-width: 1500px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const BookCard = styled.button`
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.22s ease;
  box-shadow: 0 4px 18px rgba(8, 145, 178, 0.12);
  padding: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  &:hover {
    transform: translateY(-6px) scale(1.02);
    border-color: #0891b2;
    box-shadow: 0 10px 28px rgba(8, 145, 178, 0.2);
  }
`;

const BookThumb = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #dff5fb 0%, #c9ebf5 100%);
`;

const BookImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: fill;
`;

const BookOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.55) 100%);
`;

const BookTopBadges = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 6px;
  z-index: 2;
`;

const SmallBadge = styled.span`
  padding: 3px 7px;
  border-radius: 12px;
  background: ${(props) => props.$bg || 'rgba(255,255,255,.9)'};
  color: ${(props) => props.$color || '#0891b2'};
  font-size: 8px;
  font-weight: 900;
  line-height: 1;
`;

const BookHeart = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props) => (props.$active ? '#fee2e2' : 'rgba(255,255,255,.92)')};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  z-index: 2;
`;

const BookDone = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-8deg);
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 3px solid #10b981;
  background: rgba(16, 185, 129, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 7px;
  font-weight: 900;
  color: #ffffff;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.5);
  z-index: 3;
`;

const BookLevel = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 20px;
  background: rgba(8, 145, 178, 0.88);
  color: #ffffff;
  backdrop-filter: blur(4px);
`;

const BookBody = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BookTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #172554;
  text-align: center;
  line-height: 1.4;
  min-height: 34px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BookAuthor = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #8fa3bb;
  text-align: center;
`;

const EmptyBox = styled.div`
  min-height: 240px;
  border-radius: 24px;
  border: 1.5px dashed rgba(8, 145, 178, 0.18);
  background: rgba(255, 255, 255, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-family: 'Baloo 2', sans-serif;
  font-size: 24px;
  font-weight: 700;
`;

export default function LevelUpLibrary() {
  const history = useHistory();
  const isMobileView = window.isNativeAppMobile || isMobileOnly;
  const wrapperRef = useRef(null);
  const genreScrollRef = useRef(null);
  const mobileLevelRailRef = useRef(null);
  const { lv } = queryString.parse(window.location.search.replace('?', ''));
  const [searchValue, setSearchValue] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [currentLevel, setCurrentLevel] = useState(null);
  const [level, setLevel] = useState(undefined);
  const [sortby, setSortby] = useState('lowlevel');
  const [showDoneOnly, setShowDoneOnly] = useState(false);
  const [showFavOnly, setShowFavOnly] = useState(false);

  const handleMobileSearchToggle = () => {
    const nextOpen = !mobileSearchOpen;
    setMobileSearchOpen(nextOpen);
    if (nextOpen) {
      wrapperRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
      window.scrollTo?.({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (lv) {
      setLevel(parseInt(lv, 10));
    }
  }, [lv]);

  useEffect(() => {
    if (!isMobileView || _.isNil(level) || !mobileLevelRailRef.current) return;

    const activeChip = mobileLevelRailRef.current.querySelector('[data-active="true"]');
    if (!activeChip) return;

    requestAnimationFrame(() => {
      activeChip.scrollIntoView({
        behavior: 'auto',
        inline: 'center',
        block: 'nearest',
      });
    });
  }, [isMobileView, level]);

  let getUserType = GET_STUDENT_LUPUSER;
  if (userInfoVar() && userInfoVar()?.type !== '0' && userInfoVar()?.type !== 0) {
    getUserType = GET_TEACHER_USER;
  }

  useQuery(getUserType, {
    variables: {},
    onCompleted: (data) => {
      if (!data?.getUserData) return;
      let tempData = data.getUserData;
      if (tempData.class_teacher) {
        tempData = Object.assign({}, tempData, { classes: tempData.class_teacher.map((item) => item.class) });
      } else {
        tempData = Object.assign({}, tempData, { classes: tempData.class_student.map((item) => item.class) });
      }

      if (tempData?.user_levelup_tests?.length > 0) {
        const sortedTests = _.orderBy(
          tempData.user_levelup_tests.filter((item) => item.level),
          ['test_end_date'],
          ['desc'],
        );
        const latestTest = sortedTests[0];
        if (latestTest?.lexile && latestTest?.level) {
          setCurrentLevel(latestTest.level);
          return;
        }
      }

      setCurrentLevel(tempData?.classes?.[0]?.levelup_level || '');
    },
  });

  const { data: userBook, loading, refetch } = useQuery(EXTERNAL_STUDY_BOOK, {
    variables: { type: 'levelup', level: `${level < 1 ? 'PH' : level}` },
    fetchPolicy: 'no-cache',
    skip: typeof level !== 'number',
  });

  const [favBook] = useMutation(FAVORITE_BOOK, {
    onCompleted: () => {
      refetch();
    },
  });

  const setLexileValue = (lexile = '') => {
    if (lexile.includes('BR')) return 0;
    if (lexile.includes('NP')) return 1000;
    return parseInt(lexile, 10);
  };

  const books = useMemo(() => {
    return (userBook?.getExternalUserBook || []).map((item, index) => {
      const lexileVal = !_.isNil(item?.lexile_val) ? item.lexile_val : setLexileValue(item?.lexile || '');
      const mxObj = getLixileScoreObject(lexileVal, 'A-1');
      const normalizedLevel =
        item?.mx_level !== undefined
          ? item?.mx_level === 'PH'
            ? parseInt(item?.is_interactive || 0, 10) === 0
              ? 0
              : -1
            : parseInt(item.mx_level, 10)
          : mxObj?.mx;
      return {
        ...item,
        no: index + 1,
        lexileVal,
        mxObj,
        level: normalizedLevel,
        isNew: false,
      };
    });
  }, [userBook]);

  const currentLevelBooks = useMemo(() => books.filter((item) => item.level === level), [books, level]);

  const topics = useMemo(() => {
    return [{ title: 'All', value: 'all' }].concat(
      currentLevelBooks
        .map((item) => item.category1)
        .filter(Boolean)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((value) => ({ title: value, value })),
    );
  }, [currentLevelBooks]);

  useEffect(() => {
    setSelectedTopic('all');
  }, [level]);

  const filteredBooks = useMemo(() => {
    let list = currentLevelBooks.filter((item) => {
      if (selectedTopic === 'all') return true;
      return item.category1 === selectedTopic || item.category2 === selectedTopic || item.category3 === selectedTopic;
    });

    if (showDoneOnly) {
      list = list.filter((item) => item.is_completed);
    }
    if (showFavOnly) {
      list = list.filter((item) => item.is_fav);
    }
    if (searchValue.trim()) {
      const q = searchValue.toLowerCase();
      list = list.filter((item) => (item.title || '').toLowerCase().includes(q) || (item.author || '').toLowerCase().includes(q));
    }

    if (sortby === 'title') list = _.sortBy(list, 'title');
    if (sortby === 'lowlevel') list = _.sortBy(list, 'lexile_val');
    if (sortby === 'highlevel') list = _.sortBy(list, 'lexile_val').reverse();
    if (sortby === 'fiction') list = list.filter((item) => item.genre === 'Fiction');
    if (sortby === 'nonfiction') list = list.filter((item) => item.genre === 'Non-Fiction');

    return list;
  }, [currentLevelBooks, selectedTopic, showDoneOnly, showFavOnly, searchValue, sortby]);

  const mobileLevelOptions = useMemo(
    () => [{ value: 0, label: 'mx PH' }].concat(Array.from({ length: 12 }, (_, index) => ({ value: index + 1, label: `mx ${String(index + 1).padStart(2, '0')}` }))),
    [],
  );

  const handleToggleFavorite = async (event, book) => {
    event.preventDefault();
    event.stopPropagation();
    await favBook({
      variables: {
        book_id: book?.book_no,
        type: 'levelup',
      },
    });
  };

  const handleGenreScroll = (direction) => {
    if (!genreScrollRef.current) return;
    genreScrollRef.current.scrollBy({
      left: direction * 220,
      behavior: 'smooth',
    });
  };

  const handleMobileLevelSelect = (nextLevel) => {
    setLevel(nextLevel);
    history.replace(`/level-up/library?lv=${nextLevel}`);
  };

  const formatBookLevelLabel = (book) => {
    const levelLabel = book.level === 0 || book.mx_level === 'PH' ? 'mx PH' : `mx ${String(book.level).padStart(2, '0')}`;
    const lexileLabel = book.lexileVal < 0 ? `BR${Math.abs(book.lexileVal)}L` : `${book.lexileVal}L`;
    return `${levelLabel}(${lexileLabel})`;
  };

  const currentLevelLabel = useMemo(() => {
    if (level === -1) return 'Phonics Class';
    if (level === 0) return 'Phonics Readers';
    if (level >= 1 && level <= 12) return `mx ${String(level).padStart(2, '0')} · ${LEVEL_TITLES[level]} Reader`;
    return 'Library';
  }, [level]);

  const levelBadgeValue = useMemo(() => {
    if (level === -1) return 'PH';
    if (level === 0) return 'PH';
    if (typeof level === 'number') return String(level).padStart(2, '0');
    return '--';
  }, [level]);

  const desktopContent = (
    <MainWrapper $isMobile={false}>
      <LeftMenu2
        level={level}
        setLevel={setLevel}
        isLibrary
        myLevel={parseInt(userInfoVar()?.type || 0, 10) !== 0 ? -2 : currentLevel === 'PH' ? 0 : parseInt(currentLevel, 10)}
        isStudent={parseInt(userInfoVar()?.type || 0, 10) === 0}
      />
      <Main $isMobile={false}>
        <Hero>
          <HeroBadge>
            <HeroBadgeNum>{levelBadgeValue}</HeroBadgeNum>
            <HeroBadgeLabel>mx Level</HeroBadgeLabel>
          </HeroBadge>
          <HeroInfo>
            <HeroTitle>{currentLevelLabel}</HeroTitle>
            <HeroChips>
              <HeroChip>📚 {`${currentLevelBooks.length} Books`}</HeroChip>
              <HeroChip>✅ {`${currentLevelBooks.filter((item) => item.is_completed).length} Completed`}</HeroChip>
              <HeroChip>🤍 {`${currentLevelBooks.filter((item) => item.is_fav).length} Favorite`}</HeroChip>
            </HeroChips>
          </HeroInfo>
          <HeroStats>
            <HeroStat>
              <HeroStatValue>{currentLevelBooks.length}</HeroStatValue>
              <HeroStatLabel>Total Books</HeroStatLabel>
            </HeroStat>
            <HeroStat>
              <HeroStatValue>{topics.length - 1}</HeroStatValue>
              <HeroStatLabel>Topics</HeroStatLabel>
            </HeroStat>
          </HeroStats>
        </Hero>

        <ControlBar>
          <GenreRail>
            <GenreArrowButton type="button" onClick={() => handleGenreScroll(-1)}>
              ‹
            </GenreArrowButton>
            <GenreScroll ref={genreScrollRef}>
              {topics.map((topic) => (
                <GenreTab key={topic.value} type="button" $active={selectedTopic === topic.value} onClick={() => setSelectedTopic(topic.value)}>
                  <span>{topic.title}</span>
                </GenreTab>
              ))}
            </GenreScroll>
            <GenreArrowButton type="button" onClick={() => handleGenreScroll(1)}>
              ›
            </GenreArrowButton>
          </GenreRail>
          <FilterChip type="button" $active={showDoneOnly} onClick={() => setShowDoneOnly((prev) => !prev)}>
            ✅ Completed
          </FilterChip>
          <FilterChip type="button" $active={showFavOnly} onClick={() => setShowFavOnly((prev) => !prev)}>
            🤍 Favorite
          </FilterChip>
          <SortSelect value={sortby} onChange={setSortby} bordered={false}>
            <Option value="lowlevel">Lowest</Option>
            <Option value="highlevel">Highest</Option>
            <Option value="title">A to Z</Option>
            {level !== -1 && (
              <>
                <Option value="fiction">Fiction</Option>
                <Option value="nonfiction">Non-Fiction</Option>
              </>
            )}
          </SortSelect>
          <SearchInput
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            onPressEnter={() => {}}
            allowClear
            bordered={false}
            placeholder="Search books or authors"
            suffix={<SearchOutlined style={{ color: '#64748b' }} />}
          />
        </ControlBar>

        <BookArea>
          <ResultRow>
            <span>{`총 ${filteredBooks.length}권`}</span>
          </ResultRow>
          {loading ? (
            <EmptyBox>
              <Spin />
            </EmptyBox>
          ) : filteredBooks.length ? (
            <BookGrid>
              {filteredBooks.map((book) => (
                <BookCard
                  key={`${book.book_no}-${book.code}`}
                  type="button"
                  onClick={() =>
                    history.push({
                      pathname:
                        userInfoVar()?.type > 0 && userInfoVar()?.company_name !== 'gnb'
                          ? `/level-up/description/${book.code}`
                          : `/level-up/${book?.is_voca ? 'study' : `${book?.is_interactive ? 'epub-' : ''}reader`}/${book.code}`,
                      state:
                        !book?.selfStudy && book?.assigned_idx
                          ? { bookData: { ...book, assignedIdx: book?.assigned_idx }, assigned_idx: book?.assigned_idx }
                          : { bookData: book, assigned_idx: null },
                    })
                  }
                >
                  <BookThumb>
                    <BookImg src={`https://cdn.cloubot.com/LEVELUP/covers/${book?.code}.png`} alt={book?.title} />
                    <BookOverlay />
                    <BookTopBadges>{book.isNew ? <SmallBadge $bg="rgba(249,115,22,.92)" $color="#fff">NEW</SmallBadge> : null}</BookTopBadges>
                    <BookHeart type="button" $active={!!book.is_fav} onClick={(event) => handleToggleFavorite(event, book)}>
                      {book.is_fav ? '❤️' : '🤍'}
                    </BookHeart>
                    {book.is_completed ? <BookDone><span style={{ fontSize: 22, lineHeight: 1 }}>✓</span>COMPLETED</BookDone> : null}
                    <BookLevel>{formatBookLevelLabel(book)}</BookLevel>
                  </BookThumb>
                  <BookBody>
                    <BookTitle>{book.title}</BookTitle>
                    <BookAuthor>{book.author}</BookAuthor>
                  </BookBody>
                </BookCard>
              ))}
            </BookGrid>
          ) : (
            <EmptyBox>No books for this topic.</EmptyBox>
          )}
        </BookArea>
      </Main>
    </MainWrapper>
  );

  const mobileContent = (
    <MobilePage>
      <MobileAppHeader>
        <MobileHeaderBrand>
          <MobileHeaderLogo />
          <MobileHeaderName>
            e-<span>Library</span>
          </MobileHeaderName>
        </MobileHeaderBrand>
        <MobileHeaderActions>
          <MobileHeaderRound type="button" onClick={handleMobileSearchToggle}>
            <SearchOutlined />
          </MobileHeaderRound>
          <MobileHeaderRound type="button" onClick={() => history.push('/level-up/calendar')}>
            {'\u{1F514}'}
          </MobileHeaderRound>
          <MobileHeaderRound type="button" onClick={() => history.push('/')}>
            <PoweroffOutlined />
          </MobileHeaderRound>
        </MobileHeaderActions>
      </MobileAppHeader>
      {mobileSearchOpen && (
        <MobileSearchBar>
          <SearchInput
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            allowClear
            bordered={false}
            placeholder="Search books or authors"
            suffix={<SearchOutlined style={{ color: '#64748b' }} />}
          />
        </MobileSearchBar>
      )}
      <MobilePageInner>
        <MobileTopPanel>
          <MobileLevelRail ref={mobileLevelRailRef}>
            {mobileLevelOptions.map((item) => (
              <MobileLevelChip
                key={`mobile-level-${item.value}`}
                type="button"
                $active={level === item.value}
                data-active={level === item.value ? 'true' : 'false'}
                onClick={() => handleMobileLevelSelect(item.value)}
              >
                {item.label}
              </MobileLevelChip>
            ))}
          </MobileLevelRail>

          <MobileGenreRow>
            <GenreArrowButton type="button" onClick={() => handleGenreScroll(-1)}>
              {'\u2039'}
            </GenreArrowButton>
            <MobileTopicRail ref={genreScrollRef}>
              {topics.map((topic) => (
                <GenreTab key={topic.value} type="button" $active={selectedTopic === topic.value} onClick={() => setSelectedTopic(topic.value)}>
                  <span>{topic.title}</span>
                </GenreTab>
              ))}
            </MobileTopicRail>
            <GenreArrowButton type="button" onClick={() => handleGenreScroll(1)}>
              {'\u203A'}
            </GenreArrowButton>
          </MobileGenreRow>

          <MobileFilterRow>
            <FilterChip type="button" $active={showDoneOnly} onClick={() => setShowDoneOnly((prev) => !prev)}>
              {'\u2705'} Completed
            </FilterChip>
            <FilterChip type="button" $active={showFavOnly} onClick={() => setShowFavOnly((prev) => !prev)}>
              {'\u2661'} Favorite
            </FilterChip>
          </MobileFilterRow>

          <RowFlex style={{ gap: 8 }}>
            <SortSelect value={sortby} onChange={setSortby} bordered={false} style={{ width: '44%' }}>
              <Option value="lowlevel">Lowest</Option>
              <Option value="highlevel">Highest</Option>
              <Option value="title">A to Z</Option>
              {level !== -1 && (
                <>
                  <Option value="fiction">Fiction</Option>
                  <Option value="nonfiction">Non-Fiction</Option>
                </>
              )}
            </SortSelect>
            <SearchInput
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              allowClear
              bordered={false}
              placeholder="책 제목, 저자 검색..."
              suffix={<SearchOutlined style={{ color: '#64748b' }} />}
              style={{ width: '56%' }}
            />
          </RowFlex>
        </MobileTopPanel>

        <ResultRow style={{ padding: '0 4px 4px' }}>
          <span>{`${filteredBooks.length} books found`}</span>
          <span style={{ color: '#8fa3bb', fontSize: 11 }}>{`${level === 0 ? 'mx PH' : `mx ${String(level || 0).padStart(2, '0')}`} · ${currentLevelBooks.length} books`}</span>
        </ResultRow>

        {loading ? (
          <EmptyBox>
            <Spin />
          </EmptyBox>
        ) : filteredBooks.length ? (
          <MobileBookGrid>
            {filteredBooks.map((book) => (
              <BookCard
                key={`${book.book_no}-${book.code}-mobile`}
                type="button"
                onClick={() =>
                  history.push({
                    pathname:
                      userInfoVar()?.type > 0 && userInfoVar()?.company_name !== 'gnb'
                        ? `/level-up/description/${book.code}`
                        : `/level-up/${book?.is_voca ? 'study' : `${book?.is_interactive ? 'epub-' : ''}reader`}/${book.code}`,
                    state:
                      !book?.selfStudy && book?.assigned_idx
                        ? { bookData: { ...book, assignedIdx: book?.assigned_idx }, assigned_idx: book?.assigned_idx }
                        : { bookData: book, assigned_idx: null },
                  })
                }
              >
                <BookThumb>
                  <BookImg src={`https://cdn.cloubot.com/LEVELUP/covers/${book?.code}.png`} alt={book?.title} />
                  <BookOverlay />
                  <BookHeart type="button" $active={!!book.is_fav} onClick={(event) => handleToggleFavorite(event, book)}>
                    {book.is_fav ? '\u2665' : '\u2661'}
                  </BookHeart>
                  {book.is_completed ? (
                    <BookDone>
                      <span style={{ fontSize: 22, lineHeight: 1 }}>{'\u2713'}</span>
                      COMPLETED
                    </BookDone>
                  ) : null}
                  <BookLevel>{formatBookLevelLabel(book)}</BookLevel>
                </BookThumb>
                <BookBody>
                  <BookTitle>{book.title}</BookTitle>
                  <BookAuthor>{book.author}</BookAuthor>
                </BookBody>
              </BookCard>
            ))}
          </MobileBookGrid>
        ) : (
          <EmptyBox>No books for this level.</EmptyBox>
        )}
      </MobilePageInner>

      <MobileBottomNav>
        <MobileBottomNavItem type="button" onClick={() => history.push('/level-up')}>
          <span>{'\u{1F3E0}'}</span>
          <span>Home</span>
        </MobileBottomNavItem>
        <MobileBottomNavItem type="button" $active>
          <span>{'\u{1F4DA}'}</span>
          <span>Library</span>
        </MobileBottomNavItem>
        <MobileBottomNavItem type="button" onClick={() => history.push('/level-up/portfolio')}>
          <span>{'\u{1F4C2}'}</span>
          <span>Portfolio</span>
        </MobileBottomNavItem>
        <MobileBottomNavItem type="button" onClick={() => history.push('/level-up')}>
          <span>{'\u{1F3C6}'}</span>
          <span>Ranking</span>
        </MobileBottomNavItem>
        <MobileBottomNavItem type="button" onClick={() => history.push('/level-up')}>
          <span>{'\u{1F464}'}</span>
          <span>내 정보</span>
        </MobileBottomNavItem>
      </MobileBottomNav>
    </MobilePage>
  );

  return (
    <Wrapper ref={wrapperRef} $isMobile={isMobileView}>
      {isMobileView ? mobileContent : desktopContent}
    </Wrapper>
  );
}
